import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { ShieldCheck, Loader2, RefreshCcw, CheckCircle, AlertTriangle } from 'lucide-react';
import { getPointsAudit, rescoreAllRaces } from '../../services/api';

const Status = ({ ok }) => (ok
  ? <CheckCircle size={14} className="text-green-600 inline" />
  : <AlertTriangle size={14} className="text-orange-500 inline" />);

const RaceRow = ({ r }) => (
  <tr className="border-b align-top" data-testid={`audit-race-${r.race_id}`}>
    <td className="p-1"><Status ok={r.ok} /> {r.name}{r.is_sprint_weekend && <span className="ml-1 text-[10px] text-orange-600">SPRINT</span>}</td>
    <td className="p-1">{r.has_results ? (r.is_scored ? 'Gescoord' : 'Niet gescoord') : '-'}</td>
    <td className="p-1 text-orange-700">{r.missing_fields.join(', ') || '-'}</td>
    <td className="p-1">
      {r.unscored > 0 && <div>{r.unscored} niet gescoord</div>}
      {r.duplicates > 0 && <div>{r.duplicates} dubbel</div>}
      {r.mismatches.map((m, i) => (
        <div key={i}>{m.username} ({m.type === 'race' ? 'race' : 'kwali'}): {m.stored} → {m.recomputed}</div>
      ))}
      {!r.unscored && !r.duplicates && !r.mismatches.length && '-'}
    </td>
  </tr>
);

const UserRow = ({ u }) => (
  <tr className="border-b" data-testid={`audit-user-${u.username}`}>
    <td className="p-1"><Status ok={u.total_ok && u.rescore_ok} /> {u.username}</td>
    <td className="p-1 text-right">{u.leaderboard_points}</td>
    <td className={`p-1 text-right ${u.total_ok ? '' : 'text-red-600 font-bold'}`}>{u.sum_of_predictions}</td>
    <td className={`p-1 text-right ${u.rescore_ok ? '' : 'text-orange-600 font-bold'}`}>{u.after_full_rescore}</td>
    <td className="p-1 text-right">{u.sprint_points}</td>
  </tr>
);

export const PointsAuditCard = () => {
  const { toast } = useToast();
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setBusy('audit');
    try {
      setAudit(await getPointsAudit());
    } catch (e) {
      toast({ title: 'Fout', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const rescore = async () => {
    const n = Math.max(audit?.summary.users_rescore_diff || 0, audit?.summary.users_total_mismatch || 0);
    if (!window.confirm(`Alle races opnieuw scoren? Bij ${n} deelnemer(s) verandert het totaal. Er wordt eerst een back-up gemaakt.`)) return;
    setBusy('rescore');
    try {
      const res = await rescoreAllRaces();
      toast({ title: 'Gelukt', description: res.message });
      setAudit(await getPointsAudit());
    } catch (e) {
      toast({ title: 'Fout', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const s = audit?.summary;
  return (
    <Card className="border-2 border-green-600 mb-6" data-testid="points-audit-card">
      <CardHeader>
        <CardTitle className="flex items-center"><ShieldCheck className="mr-2 text-green-600" size={20} />Puntencontrole</CardTitle>
        <CardDescription>Controleert per raceweekend of alle uitslagen zijn ingevuld en gescoord, en per deelnemer of het totaal klopt. Er wordt niets gewijzigd.</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={load} disabled={!!busy} variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="run-points-audit-btn">
          {busy === 'audit' ? <Loader2 className="mr-2 animate-spin" size={16} /> : <ShieldCheck className="mr-2" size={16} />}
          Controleer alle punten
        </Button>
        {audit && (
          <div className="mt-4 space-y-4">
            <div className="text-sm" data-testid="points-audit-summary">
              <div>{s.races_checked} raceweekenden gecontroleerd, <b>{s.races_with_issues}</b> met aandachtspunten.</div>
              <div>Klassement wijkt af van de som van de voorspellingen bij <b>{s.users_total_mismatch}</b> deelnemer(s).</div>
              <div>Volledig opnieuw scoren verandert het totaal bij <b>{s.users_rescore_diff}</b> deelnemer(s).</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="points-audit-races-table">
                <thead><tr className="border-b text-left"><th className="p-1">Race</th><th className="p-1">Status</th><th className="p-1">Ontbreekt</th><th className="p-1">Afwijkingen (opgeslagen → herberekend)</th></tr></thead>
                <tbody>{audit.races.map((r) => <RaceRow key={r.race_id} r={r} />)}</tbody>
              </table>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="points-audit-users-table">
                <thead><tr className="border-b text-left"><th className="p-1">Deelnemer</th><th className="p-1 text-right">Stand</th><th className="p-1 text-right">Som voorspellingen</th><th className="p-1 text-right">Na herscoren</th><th className="p-1 text-right">Sprintpunten</th></tr></thead>
                <tbody>{audit.users.map((u) => <UserRow key={u.username} u={u} />)}</tbody>
              </table>
            </div>
            {(s.users_rescore_diff > 0 || s.users_total_mismatch > 0) && (
              <Button onClick={rescore} disabled={!!busy} className="bg-green-600 hover:bg-green-700 text-white" data-testid="rescore-all-races-btn">
                {busy === 'rescore' ? <Loader2 className="mr-2 animate-spin" size={16} /> : <RefreshCcw className="mr-2" size={16} />}
                Alle races opnieuw scoren
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
